import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { AlertTriangle, Eye } from "lucide-react";
import DriveLinkPreviewModal from "@/components/DriveLinkPreviewModal";

const EMPTY = () => ({
  vendor_id: "", project_id: "", service_type: "", amount: "", currency: "INR",
  proforma_file_url: "", proforma_file_name: "", notes: "",
});

/**
 * Feb 2026 — Home Command Center's "Add a Quote" button. Standalone (no
 * VendorProcurement.jsx page context — the quote/PO lists are irrelevant
 * here) so it opens as a direct modal on the Dashboard. Backend
 * (`require_internal`) already lets any internal staff member raise a
 * quote, so this must never depend on a Finance/Purchases page permission.
 */
export default function AddQuoteDialog({ open, onOpenChange, onSaved }) {
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(EMPTY());
  const [docPreview, setDocPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: v }, { data: proj }] = await Promise.all([
        api.get("/procurement/vendor-options"), api.get("/projects"),
      ]);
      setVendors(v); setProjects(proj);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const submit = async () => {
    if (!form.vendor_id) { toast.error("Pick a vendor from the Registry"); return; }
    if (!form.service_type.trim()) { toast.error("Service type is required"); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter a quote amount"); return; }
    if (!form.proforma_file_url.trim()) { toast.error("Paste the proforma invoice's Google Drive link"); return; }
    try {
      await api.post("/procurement/quotes", {
        ...form, project_id: form.project_id || null, amount: Number(form.amount), proforma_file_name: "Google Drive link",
      });
      toast.success("Quote submitted · Awaiting Founder approval");
      onOpenChange(false);
      setForm(EMPTY());
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setForm(EMPTY()); }}>
      <DialogContent className="rounded-sm max-w-lg max-h-[92vh] overflow-y-auto" data-testid="add-quote-dialog-cc">
        <DialogHeader>
          <DialogTitle className="font-display">Add quote</DialogTitle>
          <DialogDescription>Every field flows into the Purchase Order once a Founder approves this quote.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-overline">Vendor *</Label>
            <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="npq-vendor"><SelectValue placeholder="Pick a registered vendor…" /></SelectTrigger>
              <SelectContent>
                {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.company || v.name} · {v.category || "—"}</SelectItem>)}
              </SelectContent>
            </Select>
            {vendors.length === 0 && (
              <p className="text-[11px] text-red-600 mt-1.5 inline-flex items-center gap-1" data-testid="npq-vendor-warning">
                <AlertTriangle size={11} />Please register this vendor in the <Link to="/studio?tab=finance&sub=vendors" className="underline">Registry</Link> before adding a quote.
              </p>
            )}
          </div>
          <div>
            <Label className="label-overline">Project</Label>
            <Select value={form.project_id || "__none"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none" ? "" : v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="npq-project"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— None —</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-overline">Service type *</Label>
            <Input className="rounded-sm mt-1" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} placeholder="e.g. Injection moulding tooling" data-testid="npq-service-type" />
          </div>
          <div>
            <Label className="label-overline">Quote amount ₹ *</Label>
            <Input type="number" min="0" className="rounded-sm mt-1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="npq-amount" />
          </div>
          <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/60">
            <Label className="label-overline">Proforma invoice — Google Drive link *</Label>
            <div className="flex items-center gap-1.5 mt-1.5">
              <Input className="rounded-sm" value={form.proforma_file_url} onChange={(e) => setForm({ ...form, proforma_file_url: e.target.value })} placeholder="https://drive.google.com/file/d/…" data-testid="npq-drive-link-input" />
              {form.proforma_file_url && (
                <button type="button" onClick={() => setDocPreview({ url: form.proforma_file_url, title: "Proforma invoice" })} className="shrink-0 text-slate-400 hover:text-[#F54800]" data-testid="npq-drive-link-preview"><Eye size={15} /></button>
              )}
            </div>
          </div>
          <div>
            <Label className="label-overline">Notes</Label>
            <Textarea rows={2} className="rounded-sm mt-1 resize-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="npq-submit">Submit for approval</Button>
        </DialogFooter>
      </DialogContent>
      <DriveLinkPreviewModal open={!!docPreview} url={docPreview?.url} title={docPreview?.title} onClose={() => setDocPreview(null)} />
    </Dialog>
  );
}
